// src/components/TriggerFlowModal/index.js — "Disparar fluxo" no chat (MessageInput)

import React, { useState, useEffect } from "react";
import { makeStyles } from "@material-ui/core/styles";
import TextField from "@material-ui/core/TextField";
import MenuItem from "@material-ui/core/MenuItem";
import CircularProgress from "@material-ui/core/CircularProgress";
import { toast } from "react-toastify";

import { i18n } from "../../translate/i18n";
import toastError from "../../errors/toastError";
import api from "../../services/api";
import AerosDialog from "../AerosDialog";
import useAerosModalStyles from "../../styles/aerosModalStyles";

const useStyles = makeStyles((theme) => ({
  loadingWrapper: { display: "flex", justifyContent: "center", alignItems: "center", minHeight: 120 },
  empty: { textAlign: "center", color: theme.palette.text.secondary, fontSize: 13, padding: 16 },
  description: { fontSize: 12.5, color: theme.palette.text.secondary, lineHeight: 1.5 },
}));

const selectMenuProps = {
  anchorOrigin: { vertical: "bottom", horizontal: "left" },
  transformOrigin: { vertical: "top", horizontal: "left" },
  getContentAnchorEl: null,
};

const TriggerFlowModal = ({ open, onClose, ticketId, onFlowTriggered, onFlowProcessing }) => {
  const classes = useStyles();
  const m = useAerosModalStyles();
  const [flows, setFlows] = useState([]);
  const [selectedFlow, setSelectedFlow] = useState("");
  const [loading, setLoading] = useState(false);
  const [loadingFlows, setLoadingFlows] = useState(true);

  // Carregar fluxos disponíveis
  useEffect(() => {
    const fetchFlows = async () => {
      if (!open) return;

      setLoadingFlows(true);
      try {
        const response = await api.get("/flowbuilder");
        // A resposta tem a estrutura: { flows: [...] }
        const flowsData = response.data.flows || [];
        // Filtrar apenas fluxos ativos
        setFlows(flowsData.filter((flow) => flow.active === true));
      } catch (error) {
        console.error("Erro ao carregar fluxos:", error);
        toastError(i18n.t("aeros.flow.modals.trigger.loadError"));
      } finally {
        setLoadingFlows(false);
      }
    };

    fetchFlows();
  }, [open]);

  // Reset ao abrir/fechar modal
  useEffect(() => {
    if (open) {
      setSelectedFlow("");
    }
  }, [open]);

  // Informar o componente pai sobre o estado de processamento
  useEffect(() => {
    if (onFlowProcessing) {
      onFlowProcessing(loading);
    }
  }, [loading, onFlowProcessing]);

  const handleTriggerFlow = async () => {
    if (!selectedFlow) {
      toast.warning(i18n.t("aeros.flow.modals.trigger.selectRequired"));
      return;
    }

    setLoading(true);
    try {
      const response = await api.post(`/tickets/${ticketId}/trigger-flow`, {
        flowId: selectedFlow,
      });

      if (response.data.success) {
        toast.success(i18n.t("triggerFlowModal.success"));
        if (onFlowTriggered) {
          onFlowTriggered(response.data.data);
        }
        onClose();
      }
    } catch (error) {
      console.error("Erro ao disparar fluxo:", error);
      const errorMessage = error.response?.data?.error || i18n.t("triggerFlowModal.error");
      toastError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      onClose();
    }
  };

  const selectedFlowInfo = selectedFlow ? flows.find((flow) => flow.id === selectedFlow) : null;

  return (
    <AerosDialog
      open={open}
      onClose={handleClose}
      width={480}
      title={i18n.t("triggerFlowModal.title")}
      onSubmit={handleTriggerFlow}
      submitLabel={i18n.t(loading ? "triggerFlowModal.triggering" : "triggerFlowModal.trigger")}
      submitDisabled={!selectedFlow || loadingFlows || flows.length === 0}
      cancelLabel={i18n.t("triggerFlowModal.cancel")}
      loading={loading}
    >
      {loadingFlows ? (
        <div className={classes.loadingWrapper}>
          <CircularProgress size={28} />
        </div>
      ) : flows.length === 0 ? (
        <div className={classes.empty}>{i18n.t("triggerFlowModal.noFlows")}</div>
      ) : (
        <>
          <div className={classes.description}>{i18n.t("triggerFlowModal.description")}</div>
          <div>
            <label className={m.fieldLabel} htmlFor="trigger-flow-select">
              {i18n.t("triggerFlowModal.selectFlow")}
            </label>
            <TextField
              id="trigger-flow-select"
              select
              className={m.input}
              variant="outlined"
              value={selectedFlow}
              onChange={(e) => setSelectedFlow(e.target.value)}
              disabled={loading}
              SelectProps={{
                displayEmpty: true,
                MenuProps: selectMenuProps,
                renderValue: (v) => {
                  const flow = flows.find((f) => f.id === v);
                  return flow ? flow.name : i18n.t("triggerFlowModal.selectFlow");
                },
              }}
            >
              {flows.map((flow) => (
                <MenuItem dense key={flow.id} value={flow.id}>
                  {flow.name}
                </MenuItem>
              ))}
            </TextField>
            {selectedFlowInfo && (
              <div className={m.hint}>
                {i18n.t("triggerFlowModal.selectedFlow")}: {selectedFlowInfo.name} · {i18n.t("triggerFlowModal.flowId")}:{" "}
                {selectedFlowInfo.id}
              </div>
            )}
          </div>
          {loading && (
            <div className={m.optionBox}>
              <CircularProgress size={18} />
              <div className={m.optionDesc}>{i18n.t("aeros.flow.modals.trigger.processing")}</div>
            </div>
          )}
        </>
      )}
    </AerosDialog>
  );
};

export default TriggerFlowModal;
